import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getPostLoginPath } from "../auth/loginNavigation";
import { otpService } from "../services/apiService";
import { toast } from 'react-toastify';
import { FaChevronDown } from "react-icons/fa";
import promoImage from "../assets/img/create-account-office.jpeg";
import { LoginPageWrapper } from "./auth/loginPageLayout";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneLogin } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [localError, setLocalError] = useState("");
  const [localSuccess, setLocalSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("authPortal", "new");
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);
    setLocalError("");
    setLocalSuccess("");
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    setLocalError("");
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLocalSuccess("");
    if (!phoneNumber || phoneNumber.length !== 10) {
      setLocalError("Please enter a valid 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const res = await otpService.sendOTP(phoneNumber);
      setOtpSent(true);
      const devOtp = res?.data?.devOtp;
      setLocalSuccess(
        devOtp
          ? `OTP (dev): ${devOtp} — Enter this code below.`
          : `OTP sent to ${phoneNumber}. Enter the code below.`
      );
      toast.success(devOtp ? `OTP (dev): ${devOtp}` : `OTP sent to ${phoneNumber}`);
      setOtp("");
    } catch (err) {
      setLocalError(err.message || "Failed to send OTP");
      toast.error(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLocalError("");
    setLocalSuccess("");
    if (!otp || otp.length !== 6) {
      setLocalError("Please enter a valid 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const result = await phoneLogin(phoneNumber, otp);
      const u = result?.user;
      const returnTo = getPostLoginPath(u, { from: location.state?.from });
      toast.success(
        result?.isNewUser ? "Welcome! You can start shopping." : "Logged in successfully"
      );
      navigate(returnTo, { replace: true });
    } catch (err) {
      setLocalError(err.message || "Invalid OTP or login failed");
      toast.error(err.message || "Invalid OTP or login failed");
    } finally {
      setLoading(false);
    }
  };

  const displayError = localError;

  return (
    <LoginPageWrapper className="auth-light-page">
      <div className="login-container">
        <div className="left-panel">
          <div className="form-wrapper">
            <div className="logo-section">
              <img loading="lazy" src="/sparepe.jpeg" alt="Sparepe" className="logo-image" />
            </div>

            <h1 className="form-title">Login</h1>
            <p className="welcome-text">Enter your mobile number to continue</p>

            {displayError && (
              <div className="error-box">
                {displayError}
              </div>
            )}

            {localSuccess && (
              <div className="success-box">
                {localSuccess}
              </div>
            )}

            <form className="login-form" onSubmit={otpSent ? handleVerifyOTP : handleSendOTP}>
              <div className="input-group">
                <label className="input-label">Phone Number</label>
                <div className="phone-input-wrapper">
                  <div className="country-code-selector">
                    <span className="country-code">+91</span>
                    <FaChevronDown className="dropdown-icon" />
                  </div>
                  <input
                    type="tel"
                    className="input-field phone-input"
                    placeholder="Enter Phone No."
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    required
                    maxLength="10"
                    disabled={otpSent}
                  />
                </div>
              </div>

              {otpSent && (
                <>
                  <div className="input-group">
                    <label className="input-label">Enter OTP</label>
                    <input
                      type="text"
                      className="input-field otp-input"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={handleOtpChange}
                      required
                      maxLength="6"
                      autoFocus
                    />
                  </div>
                  <div className="otp-actions">
                    <span 
                      className="change-number-link"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp("");
                        setLocalSuccess("");
                        setLocalError("");
                      }}
                    >
                      Change number
                    </span>
                  </div>
                </>
              )}

              <button 
                type="submit" 
                className="login-btn" 
                disabled={loading}
              >
                {loading 
                  ? (otpSent ? "Verifying..." : "Sending...") 
                  : (otpSent ? "Verify OTP" : "Get OTP")
                }
              </button>
            </form>

            <div className="alt-login-link-section">
              <span className="alt-login-text">Are you a vendor or garage? </span>
              <span
                className="alt-login-link"
                role="button"
                tabIndex={0}
                onClick={() => navigate("/login/partner", { state: location.state })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    navigate("/login/partner", { state: location.state });
                  }
                }}
              >
                Login as a vendor
              </span>
            </div>

          </div>
        </div>

        <div className="right-panel">
          <img loading="lazy" 
            src={promoImage} 
            alt="Student with books" 
            className="promo-image-full"
          />
        </div>
      </div>
    </LoginPageWrapper>
  );
};

export default Login;
